import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { LicensureDetails } from 'src/app/compliance/models/licensure-details.model';
import { StateLicensureGridItem } from 'src/app/compliance/state-licensure/grid/models/state-licensure-grid-item.model';
import { StateLicensureGridLookups } from 'src/app/compliance/state-licensure/grid/models/state-licensure-grid-lookups.model';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { StateLicensureAttachment } from '../../state-licensure/grid/models/state-licensure-details.model';
import { StateLicensureService } from '../../state-licensure/services/state-licensure.service';
import { FetchArguments2OfStateLicensureGridFilter } from '../models/fetch-arguments2of-state-licensure-grid-filter.model';
import { KeyValueDtoOfProjectTypeEnum } from '../models/key-value-dto-of-project-type-enum.model';
import { KeyValueDto } from '../models/key-value-dto.model';
import { LookupResponse } from '../models/lookup-response.model';
import { PagingTokenOfStateLicensureGridItemDto } from '../models/paging-token-of-state-licensure-grid-item-dto.model';
import { StateLicensureDto } from '../models/state-licensure-dto.model';
import { StateLicensureEditDto } from '../models/state-licensure-edit-dto.model';
import { StateLicensureFileDto } from '../models/state-licensure-file-dto.model';
import { StateLicensureClient } from '../state-licensure-client.service';
import { NSwagMigrationService } from './nswag-migration.service';

// 10 endpoints
@Injectable({ providedIn: 'root' })
export class StateLicensureNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'StateLicensureNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationStateLicensureService;
    private readonly legacy = inject(StateLicensureService);
    private readonly stateLicensureClient = inject(StateLicensureClient);

    // ** StateLicensureController.SearchPostAsync
    searchStateLicensure(args: FetchArgs | FetchArguments2OfStateLicensureGridFilter): Observable<PagingToken<StateLicensureGridItem[]> | PagingTokenOfStateLicensureGridItemDto> {
        return this.invoke(x => x
            ? this.legacy.searchStateLicensure(args as FetchArgs)
            : this.stateLicensureClient.searchPost(args as FetchArguments2OfStateLicensureGridFilter));
    }

    // ** StateLicensureController.GridLookups
    getGridLookups(): Observable<StateLicensureGridLookups | LookupResponse> {
        return this.invoke(x => x
            ? this.legacy.getGridLookups()
            : this.stateLicensureClient.gridLookups());
    }

    // ** StateLicensureController.GetDetailsByState
    getDetails(stateId): Observable<LicensureDetails[] | StateLicensureDto[]> {
        return this.invoke(x => x
            ? this.legacy.getDetails(stateId)
            : this.stateLicensureClient.getDetailsByState(stateId));
    }

    // ** StateLicensureController.GetFilesByState
    getAttachments(stateId): Observable<StateLicensureAttachment[] | StateLicensureFileDto[]> {
        return this.invoke(x => x
            ? this.legacy.getAttachments(stateId)
            : this.stateLicensureClient.getFilesByState(stateId));
    }

    // ** StateLicensureController.GetFile
    downloadAttachments(id: number, stateId: number): Observable<Blob> {
        return this.invoke(x => x
            ? this.legacy.downloadAttachments(id, stateId)
            : this.stateLicensureClient.getFile(stateId, id).pipe(map(x => x?.data)));
    }

    // ** StateLicensureController.DeleteFile
    deleteAttachment(id: number, stateId: number): Observable<any | void> {
        return this.invoke(x => x
            ? this.legacy.deleteAttachment(id, stateId)
            : this.stateLicensureClient.deleteFile(stateId, id));
    }

    // ** StateLicensureController.GetProjectTypes
    getProjectTypes(): Observable<ListItem[] | KeyValueDtoOfProjectTypeEnum[]> {
        return this.invoke(x => x
            ? this.legacy.getProjectTypes()
            : this.stateLicensureClient.getProjectTypes());
    }

    // ** StateLicensureController.UpdateDetail
    updateLic(data: LicensureDetails | StateLicensureEditDto, id: number, stateId: number): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.updateLic(data, id, stateId)
            : this.stateLicensureClient.updateDetail(stateId, id, data as StateLicensureEditDto));
    }

    // ** StateLicensureController.AddFile
    uploadFile(stateId: number, file: any): Observable<any | void> {
        const data = file as File;

        return this.invoke(x => x
            ? this.legacy.uploadFile(stateId, file)
            : this.stateLicensureClient.addFile(
                stateId as any, // stateId
                'Dummy' as any, // containerId (not used)
                { data, fileName: data.name }, // file
                null, // path (not used)
                null // fileName (not used)
            ));
    }

    // ** StateLicensureController.GetAdmins
    getAdmins(): Observable<ListItem[] | KeyValueDto[]> {
        return this.invoke(x => x
            ? this.legacy.getAdmins()
            : this.stateLicensureClient.getAdmins());
    }
}
